<template>
  <el-drawer
    title="Messages"
    :visible="visible"
    direction="rtl"
    size="460px"
    :before-close="handleClose"
  >
    <div class="quick-message-drawer">
      <p class="quick-message-drawer__note">
        Basic system status (page, time and device info) is attached
        automatically so the support team can help you faster.
      </p>

      <div class="quick-message-drawer__form">
        <el-select
          v-model="messageType"
          class="quick-message-drawer__select"
          placeholder="Message Types"
        >
          <el-option
            v-for="messageTypeItem in messageTypes"
            :key="messageTypeItem"
            :value="messageTypeItem"
            :label="messageTypeItem"
          ></el-option>
        </el-select>

        <el-input
          v-model="messageStr"
          type="textarea"
          :rows="4"
          maxlength="1000"
          show-word-limit
          placeholder="What happened?"
        ></el-input>

        <el-button
          type="primary"
          class="quick-message-drawer__send"
          :loading="sending"
          @click="sendMessage"
        >Send</el-button>
      </div>

      <div class="quick-message-drawer__history-title">Message history</div>
      <div v-if="historyLoading" class="quick-message-drawer__empty">
        Loading...
      </div>
      <div
        v-else-if="messageList.length === 0"
        class="quick-message-drawer__empty"
      >
        No messages yet.
      </div>
      <div
        v-for="(x, index) in messageList"
        :key="index"
        class="quick-message-drawer__item"
      >
        <div class="quick-message-drawer__item-type">{{ x.type }}</div>
        <div class="quick-message-drawer__item-content">
          {{ x.message_content }}
        </div>
        <div
          v-if="x.reply_content"
          class="quick-message-drawer__item-reply"
        >
          {{ "Reply：" + x.reply_content }}
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import { sendMessage, getMyMessages } from "../../api/index";
import { getUserId } from "../../utils/store";
import {
  SUPPORT_MESSAGE_TYPES,
  displayMessageType,
} from "../../utils/support-message-types";

export default {
  name: "QuickMessageDrawer",
  props: {
    visible: { type: Boolean, default: false },
    studentId: { type: [String, Number], default: "" },
    projectCode: { type: String, default: "" },
    templateName: { type: String, default: "" },
    snapshot: { type: Object, default: null },
  },
  data() {
    return {
      messageTypes: SUPPORT_MESSAGE_TYPES,
      messageType: "Message Types",
      messageStr: "",
      messageList: [],
      sending: false,
      historyLoading: false,
    };
  },
  watch: {
    visible(open) {
      if (open) {
        this.messageStr = "";
        this.messageType = "Message Types";
        this.fetchHistory();
      }
    },
  },
  methods: {
    handleClose(done) {
      if (this.sending) return;
      done();
      this.$emit("update:visible", false);
    },
    fetchHistory() {
      this.historyLoading = true;
      getMyMessages(
        this.studentId || getUserId(),
        this.projectCode
      )
        .then((res) => {
          const rows = res && res.data && res.data.data ? res.data.data : [];
          this.messageList = rows.map((row) => ({
            ...row,
            type: displayMessageType(row.type),
          }));
        })
        .catch(() => {
          this.messageList = [];
        })
        .finally(() => {
          this.historyLoading = false;
        });
    },
    buildDetails() {
      const details = {
        ...(this.snapshot || {}),
        user_agent: navigator.userAgent,
        page_url: window.location.href,
        client_time: new Date().toISOString(),
        client_time_ms: Date.now(),
        online: navigator.onLine,
      };
      return details;
    },
    sendMessage() {
      if (this.messageType === "Message Types") {
        this.$message.error("Please select a message type");
        return;
      }
      const content = String(this.messageStr || "").trim();
      if (!content) {
        this.$message.error("Please describe what happened");
        return;
      }

      this.sending = true;
      sendMessage(
        this.studentId || getUserId(),
        this.messageType,
        "student",
        content,
        this.projectCode,
        this.templateName,
        this.buildDetails()
      )
        .then(() => {
          this.$message.success("Send message successfully");
          this.messageStr = "";
          this.fetchHistory();
          this.$emit("sent");
        })
        .catch(() => {
          this.$message.error("Failed to send message");
        })
        .finally(() => {
          this.sending = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.quick-message-drawer {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 20px 20px;
  box-sizing: border-box;
  font-family: Montserrat-SemiBold, "Microsoft YaHei", sans-serif;
}
.quick-message-drawer__note {
  margin: 0 0 14px;
  padding: 10px 12px;
  border: 1px solid #dce3fd;
  border-radius: 6px;
  background: #f5f7ff;
  color: #4a526d;
  font-size: 12px;
  line-height: 1.55;
}
.quick-message-drawer__form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.quick-message-drawer__select {
  width: 100%;
}
.quick-message-drawer__send {
  align-self: flex-end;
}
.quick-message-drawer__history-title {
  margin: 18px 0 4px;
  color: #072d76;
  font-size: 14px;
  font-weight: 700;
}
.quick-message-drawer__empty {
  padding: 16px 0;
  color: #8a90a5;
  font-size: 13px;
}
.quick-message-drawer__item {
  padding: 10px 0;
  border-bottom: 1px solid #e4e7f0;
}
.quick-message-drawer__item-type {
  color: #001d9f;
  font-size: 14px;
  font-weight: 700;
}
.quick-message-drawer__item-content {
  margin-top: 6px;
  color: #33394e;
  font-size: 13px;
  line-height: 1.5;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}
.quick-message-drawer__item-reply {
  margin-top: 6px;
  color: #072d76;
  font-size: 13px;
  line-height: 1.5;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}
</style>
